/**
 * Keeping the module list where the reader left it.
 *
 * The sidebar is rendered afresh by the server on every page, so a column of
 * forty modules returns to the top each time somebody opens one. Going from
 * Levels to Economy — neighbours in the same group, both far down the list —
 * meant scrolling back down to find where you had been. Nobody reports that;
 * everybody feels it.
 *
 * Two rules, in this order:
 *
 * 1. **Put back the offset from the page before.** It is the reader's own
 *    position, and restoring it is the whole request.
 * 2. **Unless the current module is not visible there** — arriving by a direct
 *    link, or on a first visit, the remembered offset belongs to a different
 *    journey. The entry for the page being read is then brought into view.
 *
 * `sessionStorage` rather than `localStorage`: this is where somebody is in one
 * sitting, not a preference. Coming back tomorrow should start at the top.
 */

const STORE = 'bilbi.nav-scroll';

/** The scrolling element of the sidebar, which is the navigation itself. */
function column(root: ParentNode): HTMLElement | null {
  return root.querySelector<HTMLElement>('[data-nav-scroll]');
}

function remembered(): number | null {
  try {
    const raw = window.sessionStorage.getItem(STORE);
    if (!raw) return null;

    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : null;
  } catch {
    // Private windows throw outright on some browsers. A position that cannot
    // be read is simply a page that starts at the top.
    return null;
  }
}

/** Whether the active entry can be read without scrolling. */
function activeInView(nav: HTMLElement): boolean {
  const active = nav.querySelector<HTMLElement>('.is-active');
  if (!active) return true;

  const top = active.offsetTop - nav.scrollTop;
  return top >= 0 && top + active.offsetHeight <= nav.clientHeight;
}

export function initNavScroll(root: ParentNode = document): void {
  const nav = column(root);
  if (!nav) return;

  const offset = remembered();
  if (offset !== null) nav.scrollTop = offset;

  /*
   * Only when the remembered offset does not show where we are.
   *
   * Scrolling unconditionally would undo rule one on every page turn, which is
   * exactly the jumping this file exists to stop.
   */
  if (!activeInView(nav)) {
    const active = nav.querySelector<HTMLElement>('.is-active');

    // Centred rather than aligned to an edge: an entry at the very top of the
    // column reads as the first one, and the reader loses the group above it.
    if (active) nav.scrollTop = active.offsetTop - nav.clientHeight / 2;
  }

  /*
   * Written on every scroll, at most once a frame.
   *
   * The alternative — saving on the click that leaves the page — misses every
   * other way of leaving it: the browser's back button, a link in the content,
   * a keyboard shortcut.
   */
  let queued = false;

  nav.addEventListener(
    'scroll',
    () => {
      if (queued) return;
      queued = true;

      requestAnimationFrame(() => {
        queued = false;

        try {
          window.sessionStorage.setItem(STORE, String(Math.round(nav.scrollTop)));
        } catch {
          // A position that cannot be saved lasts one page. Nothing else
          // about the navigation depends on it.
        }
      });
    },
    { passive: true },
  );
}
